import { useEffect, useState } from "react";
import { useRef } from "react";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { getRecentlyViewed } from "@/lib/recentlyViewed";
import { Package, Clock, CheckCircle2, Heart, Sparkles, ChevronLeft, ChevronRight, Eye } from "lucide-react";

const SummaryCard = ({ icon: Icon, label, value, tint, testid }) => (
  <div data-testid={testid} className="rounded-2xl bg-white dark:bg-card p-4 sm:p-5 shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_28px_rgba(0,0,0,0.10)] transition-shadow flex items-center gap-4">
    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${tint}`}>
      <Icon className="w-6 h-6" />
    </div>
    <div className="min-w-0">
      <p className="text-2xl font-black font-display leading-none">{value}</p>
      <p className="text-xs text-muted-foreground mt-1 truncate">{label}</p>
    </div>
  </div>
);

const CardSkeleton = () => (
  <div className="rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.06)]">
    <div className="aspect-square shimmer" />
    <div className="p-3 space-y-2">
      <div className="h-3.5 w-4/5 rounded shimmer" />
      <div className="h-3 w-2/5 rounded shimmer" />
      <div className="h-8 w-full rounded-full shimmer mt-2" />
    </div>
  </div>
);

const SummarySkeleton = () => (
  <div className="rounded-2xl bg-white dark:bg-card p-5 shadow-[0_4px_20px_rgba(0,0,0,0.06)] flex items-center gap-4">
    <div className="w-12 h-12 rounded-2xl shimmer shrink-0" />
    <div className="flex-1 space-y-2"><div className="h-5 w-12 rounded shimmer" /><div className="h-3 w-20 rounded shimmer" /></div>
  </div>
);

const Carousel = ({ children, testid }) => {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * (ref.current.clientWidth * 0.8), behavior: "smooth" });
  return (
    <div className="relative group">
      <button aria-label="Scroll left" onClick={() => scroll(-1)} className="hidden sm:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white shadow-md items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary hover:text-white"><ChevronLeft className="w-5 h-5" /></button>
      <div ref={ref} data-testid={testid} className="flex gap-4 overflow-x-auto pb-2 scroll-smooth snap-x [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }}>
        {children}
      </div>
      <button aria-label="Scroll right" onClick={() => scroll(1)} className="hidden sm:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white shadow-md items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary hover:text-white"><ChevronRight className="w-5 h-5" /></button>
    </div>
  );
};

export const BuyerHome = ({ userName }) => {
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState(null);
  const [recs, setRecs] = useState(null);
  const [recSource, setRecSource] = useState("popular");

  useEffect(() => {
    api.get("/buyer/dashboard").then((r) => setSummary(r.data)).catch(() => setSummary({}));
    const ids = getRecentlyViewed();
    if (ids.length) api.get(`/catalog/by-ids?ids=${ids.join(",")}`).then((r) => setRecent(r.data)).catch(() => setRecent([]));
    else setRecent([]);
    api.get("/buyer/recommendations").then((r) => { setRecs(r.data.products); setRecSource(r.data.source); }).catch(() => setRecs([]));
  }, []);

  const cards = summary && [
    { icon: Package, label: "Total Orders", value: summary.total_orders ?? 0, tint: "bg-orange-100 text-orange-600", testid: "summary-total-orders" },
    { icon: Clock, label: "Pending Orders", value: summary.pending_orders ?? 0, tint: "bg-amber-100 text-amber-600", testid: "summary-pending-orders" },
    { icon: CheckCircle2, label: "Delivered", value: summary.delivered_orders ?? 0, tint: "bg-emerald-100 text-emerald-600", testid: "summary-delivered-orders" },
    { icon: Heart, label: "Wishlist Items", value: summary.wishlist_count ?? 0, tint: "bg-rose-100 text-rose-600", testid: "summary-wishlist" },
  ];

  return (
    <div className="space-y-8">
      {/* Greeting */}
      <div className="rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 text-white p-5 sm:p-6 shadow-[0_8px_28px_rgba(234,88,12,0.25)]">
        <p className="text-sm/none opacity-90">Welcome back</p>
        <h2 className="font-display font-black text-2xl sm:text-3xl mt-1">{userName || "there"} 👋</h2>
        <p className="text-sm opacity-90 mt-1">Here's what's happening with your orders today.</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="buyer-summary-cards">
        {cards ? cards.map((c) => <SummaryCard key={c.label} {...c} />) : Array.from({ length: 4 }).map((_, i) => <SummarySkeleton key={i} />)}
      </div>

      {/* Recently viewed */}
      {(recent === null || recent.length > 0) && (
        <section>
          <h3 className="font-display font-bold text-lg sm:text-xl mb-3 flex items-center gap-2"><Eye className="w-5 h-5 text-primary" />Recently Viewed</h3>
          {recent === null ? (
            <div className="flex gap-4 overflow-hidden">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="min-w-[160px] sm:min-w-[190px]"><CardSkeleton /></div>)}</div>
          ) : (
            <Carousel testid="recently-viewed-carousel">
              {recent.map((p, i) => <div key={p.product_id} className="min-w-[160px] sm:min-w-[190px] max-w-[190px] snap-start"><ProductCard product={p} index={i} /></div>)}
            </Carousel>
          )}
        </section>
      )}

      {/* Recommendations */}
      <section>
        <h3 className="font-display font-bold text-lg sm:text-xl mb-3 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />Recommended for You
          {recSource === "ai" && <span className="text-[10px] font-bold uppercase tracking-wide bg-primary/10 text-primary px-2 py-0.5 rounded-full">AI picks</span>}
        </h3>
        {recs === null ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">{Array.from({ length: 8 }).map((_, i) => <CardSkeleton key={i} />)}</div>
        ) : recs.length === 0 ? (
          <p className="text-muted-foreground text-sm py-8 text-center">No recommendations yet — add items to your wishlist to get personalised picks.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="recommendations-grid">
            {recs.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
};
